import {
  Button,
  Dialog,
  DialogFooter,
  DialogHeader,
  DialogScrollableContent,
  DialogTitle,
  Label,
  Select,
} from "@win-reality/react-ui";
import { useMemo, useState } from "react";

import { GamePlayer } from "@/types/game";

type PlayerSelectModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (player: GamePlayer) => void;
  roster: GamePlayer[];
  team: TTeamSides;
  type: "pitcher" | "catcher";
};

/**
 * PlayerSelectModal - Dialog for selecting a pitcher or catcher from the roster
 */
export const PlayerSelectModal = ({
  isOpen,
  onClose,
  onSubmit,
  roster,
  type,
}: PlayerSelectModalProps) => {
  const [selectedPlayerUuid, setSelectedPlayerUuid] = useState<string | null>(
    null,
  );

  const playerOptions = useMemo(
    () =>
      roster.map((player) => ({
        value: player.uuid,
        label: `${player.lastName}, ${player.firstName}`,
      })),
    [roster],
  );

  const handleConfirm = () => {
    if (!selectedPlayerUuid) return;

    const player = roster.find((p) => p.uuid === selectedPlayerUuid);
    if (player) {
      onSubmit(player);
      handleClose();
    }
  };

  const handleClose = () => {
    setSelectedPlayerUuid(null);
    onClose();
  };

  const title = type === "pitcher" ? "Select Pitcher" : "Select Catcher";
  const labelText = type === "pitcher" ? "Choose pitcher" : "Choose catcher";

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => !open && handleClose()}
      defaultCancelButton={false}
      size="md"
    >
      {({ showDivider, setScrollContainer }) => (
        <>
          <DialogScrollableContent setScrollContainer={setScrollContainer}>
            <DialogHeader>
              <DialogTitle>{title}</DialogTitle>
            </DialogHeader>

            <div className="flex flex-col gap-4 py-4">
              <div>
                <Label className="mb-2 block text-sm font-medium">
                  {labelText}
                </Label>
                <Select
                  value={selectedPlayerUuid ?? ""}
                  onValueChange={(value) =>
                    setSelectedPlayerUuid(String(value))
                  }
                  placeholder="Select a player..."
                  items={playerOptions}
                />
              </div>
            </div>
          </DialogScrollableContent>

          <DialogFooter showDivider={showDivider}>
            <div className="flex w-full justify-between">
              <Button
                variant="ghost"
                type="button"
                className="underline"
                onClick={handleClose}
              >
                Cancel
              </Button>
              <Button
                variant="secondary-flat-light"
                onClick={handleConfirm}
                isDisabled={!selectedPlayerUuid}
              >
                Confirm
              </Button>
            </div>
          </DialogFooter>
        </>
      )}
    </Dialog>
  );
};
